import React from "react";
import Navbar from "../../components/Navbar";
import { Check, X, ArrowRight, DollarSign, Gauge, RefreshCw } from "lucide-react";

const comparisonData = {
  chroma: {
    name: "Chroma",
    tagline: "Lightweight vector store for local-first AI development",
    pros: [
      "Extremely easy to set up and use",
      "Great for prototyping and experimentation",
      "Tight integration with LangChain",
      "Minimal infrastructure overhead",
      "Ideal for local and small-scale apps"
    ],
    cons: [
      "Not designed for large-scale production",
      "Limited distributed capabilities",
      "Performance degrades at scale",
      "Basic indexing options",
      "Not suitable for enterprise workloads"
    ],
    pricing: {
      tier: "Free / Open Source",
      description: "Completely open-source with no managed hosting costs.",
      note: "Best for: Prototypes, demos, and early-stage AI apps"
    },
    performance: {
      latency: "Fast at small scale",
      throughput: "Limited for large datasets"
    }
  },
  milvus: {
    name: "Milvus",
    tagline: "Distributed vector database built for massive scale",
    pros: [
      "Designed for billion-scale vector search",
      "Highly scalable distributed architecture",
      "Excellent performance for large AI workloads",
      "Cloud-native with Kubernetes support",
      "Strong open-source ecosystem"
    ],
    cons: [
      "Operationally complex to manage",
      "High infrastructure requirements",
      "Steep learning curve",
      "Overkill for small projects",
      "Higher operational cost at scale"
    ],
    pricing: {
      tier: "Infrastructure-Based",
      description: "Free and open-source, but infrastructure and ops costs increase with scale.",
      note: "Best for: Enterprise-grade AI platforms and production systems"
    },
    performance: {
      latency: "Optimised for distributed queries",
      throughput: "Handles massive concurrent workloads"
    }
  }
};

const migrationDetails = {
  difficulty: "Low → Medium",
  timeEstimate: "1–3 hours for 1M vectors",
  considerations: [
    "Index type and distance metric mapping",
    "Local Chroma → distributed Milvus setup",
    "Schema and metadata compatibility",
    "Infrastructure provisioning"
  ],
  howWeHelp: [
    "Automated schema and metric alignment",
    "Index and collection setup in Milvus",
    "Cluster sizing recommendations",
    "Post-migration performance tuning",
    "Data validation and integrity checks"
  ]
};

const ComparisonSection = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded-xl p-8 border border-border mb-8 shadow-sm">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
        <Icon className="w-5 h-5 text-white" />
      </div>
      <h2 className="text-2xl font-bold text-text-primary">{title}</h2>
    </div>
    {children}
  </div>
);

const ProConList = ({ items, isPositive }) => (
  <ul className="space-y-3">
    {items.map((item, idx) => (
      <li key={idx} className="flex items-start gap-3">
        <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-bg-soft border border-border">
          {isPositive ? (
            <Check className="w-3 h-3 text-green-600" />
          ) : (
            <X className="w-3 h-3 text-red-500" />
          )}
        </div>
        <span className="text-text-secondary text-sm">{item}</span>
      </li>
    ))}
  </ul>
);

const DatabaseCard = ({ data }) => (
  <div className="bg-white rounded-xl p-8 border border-border shadow-sm">
    <h3 className="text-2xl font-bold text-text-primary mb-2">{data.name}</h3>
    <p className="text-text-secondary text-sm mb-6">{data.tagline}</p>

    <div className="space-y-6">
      <div>
        <h4 className="text-text-primary font-semibold mb-3 flex items-center gap-2">
          <Check className="text-green-600 w-4 h-4" />
          Advantages
        </h4>
        <ProConList items={data.pros} isPositive />
      </div>

      <div>
        <h4 className="text-text-secondary font-semibold mb-3 flex items-center gap-2">
          <X className="text-red-500 w-4 h-4" />
          Limitations
        </h4>
        <ProConList items={data.cons} />
      </div>
    </div>
  </div>
);

const PricingCard = ({ data, name }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-xl font-bold text-text-primary">{name}</h3>
      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-bg-soft text-text-secondary border border-border">
        {data.tier}
      </span>
    </div>
    <p className="text-text-secondary text-sm mb-3">{data.description}</p>
    <p className="text-text-muted text-xs italic">{data.note}</p>
  </div>
);

const PerformanceCard = ({ data, name }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-xl font-bold text-text-primary">{name}</h3>
    </div>
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-text-secondary">Query Latency:</span>
        <span className="text-text-primary font-medium">{data.latency}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-text-secondary">Throughput:</span>
        <span className="text-text-primary font-medium">{data.throughput}</span>
      </div>
    </div>
  </div>
);

export default function ChromaVsMilvus() {
  return (
    <div className="bg-bg min-h-screen py-10 px-6">
      <Navbar />

      <div className="max-w-6xl py-28 mx-auto">

        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-text-primary mb-4">
            Chroma vs Milvus: Comparison 2026
          </h1>
          <p className="text-text-secondary text-xl max-w-3xl mx-auto">
            Lightweight local vector store vs distributed enterprise-scale engine.
            Compare scalability, performance, and operational complexity.
          </p>
        </div>

        {/* Side-by-side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <DatabaseCard data={comparisonData.chroma} />
          <DatabaseCard data={comparisonData.milvus} />
        </div>

        {/* Pricing */}
        <ComparisonSection title="Pricing Comparison" icon={DollarSign}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <PricingCard data={comparisonData.chroma.pricing} name="Chroma" />
            <PricingCard data={comparisonData.milvus.pricing} name="Milvus" />
          </div>
        </ComparisonSection>

        {/* Performance */}
        <ComparisonSection title="Performance Metrics" icon={Gauge}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <PerformanceCard data={comparisonData.chroma.performance} name="Chroma" />
            <PerformanceCard data={comparisonData.milvus.performance} name="Milvus" />
          </div>
        </ComparisonSection>

        {/* Migration */}
        <ComparisonSection title="Migration from Chroma to Milvus" icon={RefreshCw}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                Migration Complexity
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-text-secondary text-sm">Difficulty:</span>
                  <span className="text-text-primary font-semibold">{migrationDetails.difficulty}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary text-sm">Estimated Time:</span>
                  <span className="text-text-primary text-sm">{migrationDetails.timeEstimate}</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                Key Considerations
              </h3>
              <ul className="space-y-2">
                {migrationDetails.considerations.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-text-muted mt-0.5" />
                    <span className="text-text-secondary text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </ComparisonSection>

        {/* Recommendation */}
        <div className="mt-16 bg-white border border-border rounded-xl p-8 shadow-sm">
          <h2 className="text-2xl font-bold text-text-primary mb-4">
            Which Should You Choose?
          </h2>
          <div className="space-y-4 text-text-secondary">
            <p>
              <strong className="text-text-primary">Choose Chroma if:</strong> You want
              fast setup, local development, and rapid prototyping with minimal infrastructure.
            </p>
            <p>
              <strong className="text-text-primary">Choose Milvus if:</strong> You're
              building a production-scale AI system that must handle millions or billions
              of vectors efficiently.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}